import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ShoppingCart, User, Search, Menu, X, Bell, ChevronDown,
  Fish, Snowflake, Beef, Package, Drumstick,
  LogOut, Settings, Heart, ClipboardList
} from 'lucide-react';
import useCartStore from '../store/cartStore';
import { getUnreadCount } from '../api/userAPI';
import logo from '../assets/logo.png';
import './Navbar.css';

const shopCategories = [
  { label: 'Fresh Fish',        icon: Fish,      href: '/shop?category=1',  description: 'Caught daily, delivered fresh',  accent: '#0ea5e9' },
  { label: 'Frozen',            icon: Snowflake, href: '/shop?category=2',  description: 'Flash-frozen at peak quality',   accent: '#38bdf8' },
  { label: 'Premium Meat',      icon: Beef,      href: '/shop?category=3',  description: 'Premium cuts & selections',      accent: '#f97316' },
  { label: 'Shellfish',         icon: Package, href: '/shop?category=1&subcategory=12', description: 'Fresh shellfish & more', accent: '#a78bfa' },
  { label: 'French Fries',      icon: Package,   href: '/shop?category=6',  description: 'Crispy golden fries',            accent: '#eab308' },
  { label: 'Fruit Paste Puree', icon: Package,   href: '/shop?category=7',  description: 'Fresh fruit pastes & purees',   accent: '#ec4899' },
];

const Navbar = () => {
  const { fetchCart, getCartCount } = useCartStore();

  const [isLoggedIn,     setIsLoggedIn]     = useState(false);
  const [userData,       setUserData]       = useState(null);
  const [menuOpen,       setMenuOpen]       = useState(false);
  const [shopOpen,       setShopOpen]       = useState(false);
  const [userOpen,       setUserOpen]       = useState(false);
  const [searchOpen,     setSearchOpen]     = useState(false);
  const [searchQuery,    setSearchQuery]    = useState('');
  const [unreadCount,    setUnreadCount]    = useState(0);
  const [scrolled,       setScrolled]       = useState(false);
  const [mobileShopOpen, setMobileShopOpen] = useState(false);

  const navigate  = useNavigate();
  const shopRef   = useRef(null);
  const userRef   = useRef(null);
  const searchRef = useRef(null);

  useEffect(() => {
    const checkAuth = () => {
      const token = localStorage.getItem('aurora_token');
      const user = (() => {
        try {
          return JSON.parse(localStorage.getItem('aurora_user') || 'null');
        } catch {
          return null;
        }
      })();
      setIsLoggedIn(!!token);
      setUserData(user);
    };

    checkAuth();
    window.addEventListener('storage', checkAuth);
    window.addEventListener('focus', checkAuth);
    const interval = setInterval(checkAuth, 1000);
    return () => {
      window.removeEventListener('storage', checkAuth);
      window.removeEventListener('focus', checkAuth);
      clearInterval(interval);
    };
  }, []);

  /* ── Data Fetching ────────────────────── */
  const fetchUnreadCount = async () => {
    try {
      const response = await getUnreadCount();
      setUnreadCount(response.data);
    } catch {}
  };

  useEffect(() => {
    if (isLoggedIn) {
      fetchCart();
      fetchUnreadCount();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);
  /* ── Scroll Detection ─────────────────── */
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  /* ── Outside Click ────────────────────── */
  useEffect(() => {
    const onMouseDown = (e) => {
      if (shopRef.current && !shopRef.current.contains(e.target))
        setShopOpen(false);
      if (userRef.current && !userRef.current.contains(e.target))
        setUserOpen(false);
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, []);

  /* ── Auto-focus Search ────────────────── */
  useEffect(() => {
    if (searchOpen && searchRef.current) searchRef.current.focus();
  }, [searchOpen]);

  /* ── Lock body scroll when mobile menu open ── */
  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  /* ── Handlers ─────────────────────────── */
  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/shop?search=${encodeURIComponent(searchQuery)}`);
      setSearchOpen(false);
      setSearchQuery('');
    }
  };

  const handleDashboard = () => {
    window.location.href = 'https://app.auroragh.com/dashboard';
  };

  const handleLogin = () => {
    window.location.href = 'https://app.auroragh.com/login';
  };

  const handleRegister = () => {
    window.location.href = 'https://auroragh.com/register';
  };

const handleLogout = () => {
  localStorage.removeItem('aurora_token');
  localStorage.removeItem('aurora_user');
  setIsLoggedIn(false);
  setUserData(null);
  setUserOpen(false);
  // Force Next.js to clear its state by going to logout route
  window.location.href = 'https://app.auroragh.com/logout';
};

  const closeMenu = () => setMenuOpen(false);
  const cartCount = getCartCount();

  return (
    <>
      {/* ══ NAVBAR ══════════════════════════════════════════ */}
      <nav className={`navbar${scrolled ? ' navbar--scrolled' : ''}`}>
        <div className="navbar-inner">

          {/* Logo */}
          <Link to="/" className="navbar-logo">
            <img src={logo} alt="Aurora Seafood" className="navbar-logo-img" />
          </Link>

          {/* Desktop Nav Links */}
          <div className="navbar-links">
            <Link to="/" className="nav-link">Home</Link>

            {/* Shop Dropdown */}
            <div className="nav-dropdown-wrap" ref={shopRef}>
              <button
                className={`nav-link${shopOpen ? ' nav-link--active' : ''}`}
                onClick={() => setShopOpen(!shopOpen)}
                aria-expanded={shopOpen}
              >
                Shop
                <ChevronDown size={14} className={`dropdown-chevron${shopOpen ? ' open' : ''}`} />
              </button>

              <div className={`shop-dropdown${shopOpen ? ' shop-dropdown--open' : ''}`}>
                <div className="shop-dropdown-header">
                  <span>Browse Categories</span>
                  <Link to="/shop" className="shop-view-all" onClick={() => setShopOpen(false)}>
                    View all →
                  </Link>
                </div>
                <div className="shop-dropdown-grid">
                  {shopCategories.map((cat) => {
                    const Icon = cat.icon;
                    return (
                      <Link
                        key={cat.label}
                        to={cat.href}
                        className="shop-cat-item"
                        onClick={() => setShopOpen(false)}
                        style={{ '--cat-accent': cat.accent }}
                      >
                        <span className="shop-cat-icon"><Icon size={20} /></span>
                        <span className="shop-cat-text">
                          <span className="shop-cat-label">{cat.label}</span>
                          <span className="shop-cat-desc">{cat.description}</span>
                        </span>
                      </Link>
                    );
                  })}
                </div>
              </div>
            </div>

            <Link to="/shop?class=ECONOMY"  className="nav-link">Economy</Link>
            <Link to="/shop?class=BUSINESS" className="nav-link">Business</Link>
            <Link to="/shop?class=FIRST"    className="nav-link">First Class</Link>
            <Link to="/features" className="nav-link nav-link--features">Features</Link>
            <Link to="/recipes"  className="nav-link nav-link--features">Recipes</Link>
          </div>

          {/* Right Actions */}
          <div className="navbar-actions">

            {/* Search */}
            <button
              className="icon-btn"
              onClick={() => setSearchOpen(!searchOpen)}
              aria-label="Search"
            >
              <Search size={18} />
            </button>

            {/* Cart */}
            <Link to="/cart" className="icon-btn" aria-label="Cart">
              <ShoppingCart size={18} />
              {cartCount > 0 && (
                <span className="badge badge--blue">{cartCount}</span>
              )}
            </Link>

            {isLoggedIn ? (
              <>
                {/* Notifications */}
                <button
                  className="icon-btn"
                  onClick={handleDashboard}
                  aria-label="Notifications"
                >
                  <Bell size={18} />
                  {unreadCount > 0 && (
                    <span className="badge badge--red">{unreadCount}</span>
                  )}
                </button>

                <div className="navbar-divider" />

                {/* User Menu */}
                <div className="user-menu" ref={userRef}>
                  <button
                    className={`user-btn${userOpen ? ' user-btn--active' : ''}`}
                    onClick={() => setUserOpen(!userOpen)}
                    aria-expanded={userOpen}
                  >
                    <span className="user-avatar">
                      {userData?.firstName?.[0]?.toUpperCase() || <User size={14} />}
                    </span>
                    <span className="user-name">{userData?.firstName}</span>
                    <ChevronDown
                      size={12}
                      className={`dropdown-chevron${userOpen ? ' open' : ''}`}
                    />
                  </button>

                  <div className={`user-dropdown${userOpen ? ' user-dropdown--open' : ''}`}>
                    <div className="user-dropdown-header">
                      <span className="user-avatar user-avatar--lg">
                        {userData?.firstName?.[0]?.toUpperCase() || <User size={18} />}
                      </span>
                      <div className="ud-info">
                        <p className="ud-name">
                          {userData?.firstName} {userData?.lastName}
                        </p>
                        <p className="ud-email">{userData?.email}</p>
                      </div>
                    </div>
                    <div className="ud-divider" />
                    <button className="ud-item" onClick={handleDashboard}>
                      <User size={15} /> My Account
                    </button>
                    <button className="ud-item" onClick={() => {
                      window.location.href = 'https://app.auroragh.com/dashboard/orders';
                    }}>
                      <ClipboardList size={15} /> My Orders
                    </button>
                    <button className="ud-item" onClick={() => {
                      window.location.href = 'https://app.auroragh.com/dashboard/wishlist';
                    }}>
                      <Heart size={15} /> Wishlist
                    </button>
                    <button className="ud-item" onClick={() => {
                      window.location.href = 'https://app.auroragh.com/dashboard/profile';
                    }}>
                      <Settings size={15} /> Settings
                    </button>
                    <div className="ud-divider" />
                    <button
                      className="ud-item ud-item--danger"
                      onClick={handleLogout}
                    >
                      <LogOut size={15} /> Logout
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className="navbar-divider" />
                <div className="auth-btns">
                  <button className="btn-ghost" onClick={handleLogin}>
                    Login
                  </button>
                  <button className="btn-solid" onClick={handleRegister}>
                    Register
                  </button>
                </div>
              </>
            )}

            {/* Mobile Toggle */}
            <button
              className="mobile-toggle"
              onClick={() => setMenuOpen(!menuOpen)}
              aria-label="Toggle menu"
              aria-expanded={menuOpen}
            >
              {menuOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>{/* end navbar-actions */}
        </div>{/* end navbar-inner */}

        {/* Search Panel */}
        <div className={`search-panel${searchOpen ? ' search-panel--open' : ''}`}>
          <form onSubmit={handleSearch} className="search-form">
            <Search size={18} className="search-icon" />
            <input
              ref={searchRef}
              type="text"
              placeholder="Search for seafood, meat, chicken…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
            />
            {searchQuery && (
              <button
                type="button"
                className="search-clear"
                onClick={() => setSearchQuery('')}
              >
                <X size={14} />
              </button>
            )}
            <button type="submit" className="search-submit">Search</button>
          </form>
        </div>
      </nav>

      {/* ══ MOBILE BACKDROP ══════════════════════════════════ */}
      {menuOpen && (
        <div
          className="mobile-backdrop"
          onClick={closeMenu}
          aria-hidden="true"
        />
      )}

      {/* ══ MOBILE MENU ══════════════════════════════════════ */}
      <div
        className={`mobile-menu${menuOpen ? ' mobile-menu--open' : ''}`}
        aria-hidden={!menuOpen}
      >
        <div className="mobile-menu-inner">

          {/* Mobile Header */}
          <div className="mm-header">
            <img src={logo} alt="Aurora Seafood" className="mm-logo-img" />
            <button
              className="mm-close"
              onClick={closeMenu}
              aria-label="Close menu"
            >
              <X size={18} />
            </button>
          </div>

          {/* User Card (if logged in) */}
          {isLoggedIn && (
            <div className="mm-user-card">
              <span className="user-avatar user-avatar--lg">
                {userData?.firstName?.[0]?.toUpperCase() || <User size={18} />}
              </span>
              <div className="mm-user-info">
                <div className="mm-user-name">
                  {userData?.firstName} {userData?.lastName}
                </div>
                <div className="mm-user-email">{userData?.email}</div>
              </div>
            </div>
          )}

          {/* Nav Links */}
          <Link to="/" className="mm-link" onClick={closeMenu}>Home</Link>

          {/* Shop accordion */}
          <div className="mm-section">
            <button
              className="mm-link"
              onClick={() => setMobileShopOpen(!mobileShopOpen)}
            >
              <span>Shop</span>
              <ChevronDown
                size={16}
                className={`dropdown-chevron${mobileShopOpen ? ' open' : ''}`}
              />
            </button>
            <div className={`mm-shop-cats${mobileShopOpen ? ' mm-shop-cats--open' : ''}`}>
              {shopCategories.map((cat) => {
                const Icon = cat.icon;
                return (
                  <Link
                    key={cat.label}
                    to={cat.href}
                    className="mm-cat"
                    onClick={closeMenu}
                    style={{ '--cat-accent': cat.accent }}
                  >
                    <span className="mm-cat-icon"><Icon size={16} /></span>
                    {cat.label}
                  </Link>
                );
              })}
            </div>
          </div>

          <Link to="/shop?class=ECONOMY"  className="mm-link" onClick={closeMenu}>
            Economy
          </Link>
          <Link to="/shop?class=BUSINESS" className="mm-link" onClick={closeMenu}>
            Business
          </Link>
          <Link to="/shop?class=FIRST"    className="mm-link" onClick={closeMenu}>
            First Class
          </Link>
          <Link to="/features" className="mm-link" onClick={closeMenu}>Features</Link>
          <Link to="/recipes" className="mm-link" onClick={closeMenu}>Recipes</Link>

          <div className="mm-divider" />

          {isLoggedIn ? (
            <>
              <button
                className="mm-link"
                onClick={() => {
                  window.location.href = 'https://app.auroragh.com/dashboard';
                  closeMenu();
                }}
              >
                <span>My Account</span>
              </button>
              <button
                className="mm-link"
                onClick={() => {
                  window.location.href = 'https://app.auroragh.com/dashboard/orders';
                  closeMenu();
                }}
              >
                <span>My Orders</span>
              </button>
              <button
                className="mm-link"
                onClick={() => {
                  window.location.href = 'https://app.auroragh.com/dashboard/wishlist';
                  closeMenu();
                }}
              >
                <span>Wishlist</span>
              </button>
              <div className="mm-divider" />
              <button
                className="mm-link mm-link--danger"
                onClick={() => { handleLogout(); closeMenu(); }}
              >
                Logout
              </button>
            </>
          ) : (
            <div className="mm-auth">
              <button
                className="btn-ghost btn-ghost--full"
                onClick={() => { handleLogin(); closeMenu(); }}
              >
                Login
              </button>
              <button
                className="btn-solid btn-solid--full"
                onClick={() => { handleRegister(); closeMenu(); }}
              >
                Register
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Navbar;